import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,

  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Size, SizeListResponse } from '../types';
import { apiService } from '../services/api';
import LoadingButton from '../components/LoadingButton';
import SizeDialog from '../components/SizeDialog';
import SizeParametersDialog from '../components/SizeParametersDialog';

const SizesPage: React.FC = () => {
  const { t } = useTranslation();
  const [sizes, setSizes] = useState<Size[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [parametersDialogOpen, setParametersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState<Size | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSizes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: SizeListResponse = await apiService.getSizes(true);
      setSizes(response.sizes);
    } catch (err: any) {
      console.error('Error loading sizes:', err);
      setError(err.detail || 'Error loading sizes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSizes();
  }, []);

  const handleCreateSize = () => {
    setSelectedSize(null);
    setCreateDialogOpen(true);
  };

  const handleEditSize = (size: Size) => {
    setSelectedSize(size);
    setEditDialogOpen(true);
  };

  const handleEditParameters = (size: Size) => {
    setSelectedSize(size);
    setParametersDialogOpen(true);
  };

  const handleDeleteSize = (size: Size) => {
    setSelectedSize(size);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSize) return;

    try {
      setDeleting(true);
      await apiService.deleteSize(selectedSize.id);
      await loadSizes();
      setDeleteDialogOpen(false);
      setSelectedSize(null);
    } catch (err: any) {
      console.error('Error deleting size:', err);
      setError(err.detail || 'Error deleting size');
    } finally {
      setDeleting(false);
    }
  };

  const formatParameterValue = (paramName: string, value: string): string => {
    // For numeric values, apply formatting
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && paramName.includes('records')) {
      return numValue >= 1000000 ? `${(numValue / 1000000).toFixed(1)}M` : `${(numValue / 1000).toFixed(0)}K`;
    }
    // Return as-is for non-numeric or other values (e.g., "250m", "1Gi")
    return value;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('sizes.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateSize}
        >
          {t('sizes.createSize')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {sizes.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {t('sizes.noSizes')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSize}
              sx={{ mt: 2 }}
            >
              {t('sizes.createSize')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {sizes.map((size) => (
            <Grid item xs={12} md={6} lg={4} key={size.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2">
                      {size.display_name}
                    </Typography>
                    <Chip
                      label={size.is_active ? t('common.active') : t('common.inactive')}
                      color={size.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
                    {size.name}
                  </Typography>

                  {size.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      {size.description}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    {t('sizes.parameters')}:
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {size.parameters.map((param) => (
                      <Box key={param.parameter_name} display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="textSecondary">
                          {t(`sizes.defaultParameters.${param.parameter_name}`) || param.parameter_name}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {formatParameterValue(param.parameter_name, param.parameter_value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Tooltip title={t('sizes.editParameters')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditParameters(size)}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSize(size)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSize(size)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Size Dialog */}
      <SizeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSizeCreated={loadSizes}
      />

      {/* Edit Size Dialog */}
      <SizeDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSizeUpdated={loadSizes}
        size={selectedSize}
      />

      {/* Edit Parameters Dialog */}
      <SizeParametersDialog
        open={parametersDialogOpen}
        onClose={() => setParametersDialogOpen(false)}
        onParametersUpdated={loadSizes}
        size={selectedSize}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('sizes.deleteSize')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('sizes.confirmDelete', { name: selectedSize?.display_name })}
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            {t('sizes.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <LoadingButton
            onClick={handleConfirmDelete}
            loading={deleting}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SizesPage;
